import * as focusTrap from 'focus-trap';

export default class SearchForm {

  constructor(searchForm, searchButtons) {

    let searchInput, searchActive = false,
      searchTrap, firstTab, lastTab, hasStickyScroll;

    this.init = () => {
      hasStickyScroll = document.documentElement.classList.contains('sticky-scroll');
      searchInput = searchForm.querySelector('input');
      firstTab = searchInput;
      lastTab = searchForm.querySelector('.btn.search-toggle.close') || null;
      searchTrap = focusTrap.createFocusTrap( searchForm, {
        escapeDeactivates: false,
        clickOutsideDeactivates: true,
        fallbackFocus: searchButtons[0],
        setReturnFocus,
        initialFocus: '.search-trap-initial'
      });

      searchButtons.forEach(btn => btn.addEventListener('click', toggleSearch));
      searchButtons.forEach(btn => btn.addEventListener('keyup', (e) => {
        if (13 === e.keyCode) {
          toggleSearch(e);
        }
      }));

      // Update search position when header position changes (for sticky scroll)
      if (hasStickyScroll) {
        window.addEventListener('scroll', handleScroll, { passive: true });
      }
    };

    function setReturnFocus() {
      return searchButtons[0] ?? true;
    }

    function toggleSearch(e) {
      if (e) e.preventDefault();
      toggleState();
    }

    function handleScroll() {
      // Only handle scroll if search is active
      if (!searchActive) return;

      // Match the transition of the header for smooth movement
      const navMainWrapY = getComputedStyle(document.documentElement).getPropertyValue('--nav-main-wrap-y').trim();
      if (navMainWrapY) {
        // Ensure the search form uses the same transform as the header
        searchForm.style.transform = `translateY(${navMainWrapY})`;
      }
    }

    // -- event handlers --
    // shift-tab out of search
    function firstTabDeactivate(event) {

      if ( event.key === 'Tab' && event.shiftKey ) {

        if ( firstTab ) firstTab.classList.add('search-trap-initial');
        if ( lastTab ) lastTab.classList.remove('search-trap-initial');

        // Restore focusability of navigation items.
        searchTrap.deactivate({
          onPostDeactivate: () => {
            if ( firstTab ) firstTab.removeEventListener('keydown', firstTabDeactivate);
            toggleState();
          }
        });
      }
    }
    // tab out of search to next menu item
    function lastTabDeactivate(event) {
      if (event.key === 'Tab' && !event.shiftKey) {

        if ( lastTab ) lastTab.classList.add('search-trap-initial');
        if ( firstTab ) firstTab.classList.remove('search-trap-initial');

        // Restore focusability of navigation items.
        searchTrap.deactivate({
          onPostDeactivate: () => {
            if ( lastTab ) lastTab.removeEventListener('keydown', lastTabDeactivate);
            toggleState();
          }
        });
      }
    }
    function toggleState() {
      switch (searchActive) {
        // open search
        case false:
          if (!hasStickyScroll) {
            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
          searchForm.classList.add('active');

          // If sticky scroll is enabled, apply initial transform
          if (hasStickyScroll) {
            const navMainWrapY = getComputedStyle(document.documentElement).getPropertyValue('--nav-main-wrap-y').trim();
            if (navMainWrapY) {
              searchForm.style.transform = `translateY(${navMainWrapY})`;
            }
          }

          // trap focus
          searchTrap.activate({
            onPostActivate: () => {
              setTimeout(() => {
                if ( firstTab ) firstTab.focus()
                }, 10);
            }
          });
          if ( firstTab ) firstTab.addEventListener('keydown', firstTabDeactivate);
          if ( lastTab ) lastTab.addEventListener('keydown', lastTabDeactivate);
          searchActive = true;
          // close on click outside
          searchForm.addEventListener('keyup', closeOnEscKey);
          document.addEventListener('click', closeSearch);
          break;
        // close search
        case true:
          searchForm.classList.remove('active');

          // Reset transform when closing
          if (hasStickyScroll) {
            searchForm.style.transform = '';
          }

          searchActive = false;
          searchTrap.deactivate({
            returnFocus: true
          });
          // remove document event listener
          document.removeEventListener('click', closeSearch);
          if ( firstTab ) {
            firstTab.removeEventListener('keydown', firstTabDeactivate);
            firstTab.classList.remove('search-trap-last-item');
          }
          if ( lastTab ) {
            lastTab.removeEventListener('keydown', lastTabDeactivate);
            lastTab.classList.remove('search-trap-last-item');
          }
          searchForm.removeEventListener('keyup', closeOnEscKey);
        break;
      }
    }
    function closeOnEscKey(e) {
      if (27 === e.keyCode && searchActive) toggleSearch(null);
    }

    function closeSearch(e) {
      // if click is on search button, return
      // in case searchForm does not contain the button
      for (let i = 0; i < searchButtons.length; i++) {
        if ( searchButtons[i] === e.target ) return;
      }
      if (searchActive && !searchForm.contains(e.target)) {
        toggleSearch(null);
      }
    }
  }
}